import { useSelector } from 'react-redux';
import { JsonObj } from '@mhp/general-interface';
import {
  accountLoadingSelector,
  currentAccountConsumerSelector,
  isImpersonatingSelector
} from 'store/account/selectors';
import {
  familyPlanBenefitsSelector,
  isFetchingSelector,
  myPlanBenefitsSelector
} from 'store/cost/selectors';
import { getGlossaries } from 'store/glossary/selectors';
import {
  hasActiveSHInsurancesSelector,
  profileIsFetchingSelector,
  selectHealthFacetsInsuranceSelector
} from 'store/profile/selectors';
import { Insurance } from 'store/profile/types';
import {
  getBillsSelector,
  getBillingSummarySelector,
  getPaymentsSummarySelector,
  getHsaSummarySelector,
  getPaymentPlanSummarySelector,
  getFinancialAssistanceSummarySelector,
  getFinancialAssistanceApplicationsSelector,
  getFinancialAssistanceApplicationSelector
} from 'store/billing/selectors';
import {
  BillingHomeInterface,
  BillingSummary,
  HsaSummary,
  PaymentPlanSummary,
  PaymentsSummary
} from 'store/billing/types';

export const useBillingHome = (): BillingHomeInterface => {
  const profileLoading = useSelector(profileIsFetchingSelector);
  const accountLoading = useSelector(accountLoadingSelector);
  const switchingAccount = profileLoading || accountLoading;
  const isImpersonating = useSelector(isImpersonatingSelector);
  const shInsurance = useSelector(selectHealthFacetsInsuranceSelector);
  const familyBenefits = useSelector(familyPlanBenefitsSelector);
  const currentConsumer = useSelector(currentAccountConsumerSelector);
  const isFetching = useSelector(isFetchingSelector);
  const isActiveSHInsurance = useSelector(hasActiveSHInsurancesSelector);
  const myBenefits = useSelector(myPlanBenefitsSelector);

  const glossary = useSelector(getGlossaries);
  const {
    data: guarantorData,
    error: guarantorError,
    isFetching: guarantorLoading = false
  } = useSelector(getBillingSummarySelector);

  const { data: billsData, error: billsError, isFetching: billsLoading = false } = useSelector(
    getBillsSelector
  );

  const {
    data: paymentsSummary,
    error: collectionError,
    isFetching: collectionLoading = false
  } = useSelector(getPaymentsSummarySelector);

  const { data: hsaData, isFetching: hsaLoading, error: hsaError } = useSelector(
    getHsaSummarySelector
  );

  const {
    data: financialAssistanceSummary,
    error: financialAssistanceSummaryError,
    isFetching: financialAssistanceSummaryLoading = false
  } = useSelector(getFinancialAssistanceSummarySelector);

  const {
    data: financialAssistanceApplications,
    error: financialAssistanceApplicationsError,
    isFetching: financialAssistanceApplicationsLoading = false
  } = useSelector(getFinancialAssistanceApplicationsSelector);

  const {
    data: financialAssistanceApplication,
    error: financialAssistanceApplicationError,
    isFetching: financialAssistanceApplicationLoading = false
  } = useSelector(getFinancialAssistanceApplicationSelector);

  const { paymentPlansData, paymentPlansError, paymentPlansLoading } = usePaymentPlans();

  return {
    collectionError,
    collectionLoading,
    currentConsumer,
    familyBenefits,
    glossary,
    guarantorData,
    guarantorError,
    guarantorLoading,
    hsaData,
    hsaError,
    hsaLoading,
    isActiveSHInsurance: !!isActiveSHInsurance,
    isFetching: !!isFetching,
    isImpersonating,
    myBenefits,
    paymentPlansData,
    paymentPlansError,
    paymentPlansLoading,
    paymentsSummary,
    shInsurance,
    switchingAccount,
    billsData,
    billsError,
    billsLoading,
    financialAssistanceSummary,
    financialAssistanceSummaryError,
    financialAssistanceSummaryLoading,
    financialAssistanceApplications,
    financialAssistanceApplicationsError,
    financialAssistanceApplicationsLoading,
    financialAssistanceApplication,
    financialAssistanceApplicationError,
    financialAssistanceApplicationLoading
  };
};

export const useBillsCollapsibleList = (): {
  paymentsSummary?: PaymentsSummary;
  guarantorData?: BillingSummary;
  isImpersonating?: boolean;
} => {
  const isImpersonating = useSelector(isImpersonatingSelector);
  const { data: guarantorData } = useSelector(getBillingSummarySelector);

  const { data: paymentsSummary } = useSelector(getPaymentsSummarySelector);

  return {
    paymentsSummary,
    guarantorData,
    isImpersonating
  };
};

export const usePaymentPlans = (): {
  paymentPlansData?: PaymentPlanSummary;
  paymentPlansError?: Error | string | null;
  paymentPlansLoading?: boolean;
} => {
  const {
    data: paymentPlansData,
    error: paymentPlansError,
    isFetching: paymentPlansLoading = false
  } = useSelector(getPaymentPlanSummarySelector);

  return {
    paymentPlansData,
    paymentPlansError,
    paymentPlansLoading
  };
};

export const usePaymentsCollapsibleList = (): {
  paymentsSummary?: PaymentsSummary;
  isImpersonating?: boolean;
} => {
  const isImpersonating = useSelector(isImpersonatingSelector);
  const { data: paymentsSummary } = useSelector(getPaymentsSummarySelector);

  return {
    paymentsSummary,
    isImpersonating
  };
};

export const useHSACollapsibleList = (): {
  familyBenefits?: JsonObj;
  hsaData?: HsaSummary;
  hsaLoading?: boolean;
  myBenefits?: JsonObj;
  isImpersonating?: boolean;
  selectHealthFacetsInsurance?: Insurance;
} => {
  const isImpersonating = useSelector(isImpersonatingSelector);
  const { data: hsaData, isFetching: hsaLoading } = useSelector(getHsaSummarySelector);

  const selectHealthFacetsInsurance = useSelector(selectHealthFacetsInsuranceSelector);
  const myBenefits = useSelector(myPlanBenefitsSelector);
  const familyBenefits = useSelector(familyPlanBenefitsSelector);

  return {
    familyBenefits,
    hsaData,
    hsaLoading,
    isImpersonating,
    myBenefits,
    selectHealthFacetsInsurance
  };
};
